import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import {
  CompanyPanel,
  EarningsPanel,
  NewsPanel,
  StockCard,
  WatchlistStatus,
} from "@/features/stocks/StockDashboardParts";
import { useStockDetails, useStockMarket } from "@/features/stocks/useStockMarket";
import type { StockSymbol } from "@/features/stocks/types";

export default function StocksDashboard() {
  const { data, loading, refreshing, error, refresh } = useStockMarket();
  const [symbol, setSymbol] = useState<StockSymbol>("AAPL");
  const { news, earnings, loading: detailsLoading } = useStockDetails(symbol);

  useEffect(() => {
    document.title = "Aktien · CashFUX";
  }, []);

  const selectedQuote = data?.items.find((item) => item.symbol === symbol) ?? null;

  return (
    <DashboardShell
      title="Aktien"
      subtitle="US-Aktien-Watchlist mit echten Finnhub-Daten"
    >
      <div className="market-dashboard text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Aktien-Watchlist</h1>
            <p className="mt-1 text-sm text-white/40">
              Kurse, Unternehmensdaten, Earnings und Nachrichten
            </p>
          </div>
          {data && (
            <WatchlistStatus data={data} refreshing={refreshing} refresh={() => void refresh()} />
          )}
        </div>
        {loading && (
          <div className="mt-8 grid animate-pulse gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 border border-white/10 bg-[#07080d]" />
            ))}
          </div>
        )}
        {error && (
          <div className="mt-8 border-l-4 border-[#e01e37] bg-[#07080d] p-5">
            <p className="font-semibold">Aktienkurse nicht verfügbar</p>
            <p className="mt-1 text-sm text-white/40">{error}</p>
            <button
              onClick={() => void refresh()}
              className="mt-4 h-10 bg-[#e01e37] px-4 text-sm font-semibold text-white"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {data && (
          <div className="mt-8 space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {data.items.map((quote) => (
                <StockCard
                  key={quote.symbol}
                  quote={quote}
                  selected={quote.symbol === symbol}
                  onSelect={() => setSymbol(quote.symbol)}
                />
              ))}
            </section>
            {!data.items.length && (
              <div className="border border-dashed border-white/10 bg-[#07080d] p-10 text-center text-white/40">
                Keine Aktienkurse verfügbar.
              </div>
            )}
            {selectedQuote && <CompanyPanel quote={selectedQuote} />}
            <div className="grid gap-6 lg:grid-cols-2">
              <EarningsPanel earnings={earnings} loading={detailsLoading} />
              <NewsPanel news={news} loading={detailsLoading} />
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
